import React from 'react';
import {
  Box,
  Container,
  Stack,
  Typography,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import { Icon } from '@iconify/react';

interface TestimonialProps {
  name: string;
  role: string;
  company: string;
  avatar: string;
  quote: string;
}

const TestimonialCard: React.FC<TestimonialProps> = ({
  name,
  role,
  company,
  avatar,
  quote,
}) => (
  <Card
    sx={{
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '16px',
      height: '100%',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'transform 0.3s ease',
      '&:hover': {
        transform: 'translateY(-5px)',
      },
    }}
  >
    <CardContent
      sx={{
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', mb: 3 }}>
          <Icon
            icon='mdi:format-quote-open'
            width='24'
            height='24'
            style={{ color: 'primary.main', marginRight: '8px' }}
          />
        </Box>
        <Typography
          variant='body1'
          sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 3, fontStyle: 'italic' }}
        >
          "{quote}"
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Avatar
          src={avatar}
          alt={name}
          sx={{
            width: 50,
            height: 50,
            bgcolor: 'primary.main',
            mr: 2,
            color: 'text.primary',
          }}
        >
          {/* Fallback if no avatar image */}
          {name.charAt(0)}
        </Avatar>
        <Box>
          <Typography
            variant='subtitle1'
            sx={{ color: '#fff', fontWeight: 600 }}
          >
            {name}
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
          >
            {role}, {company}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const TestimonialsSection: React.FC = () => {
  const testimonials = [
    {
      name: 'Yousef Asfour',
      role: 'Organizer',
      company: 'TEDx Hashemite University',
      avatar: '',
      quote:
        'Skyfall Enterprises was instrumental in making TEDxHashemiteUniversity a success. Their technical expertise and support brought our vision to life effortlessly.',
    },
    {
      name: 'Malik Meqdad',
      role: 'Co-Founder',
      company: 'RenWater',
      avatar: '',
      quote:
        "Skyfall Enterprises played a key role in elevating RenWater's digital presence. Their expertise in web support, performance optimization, and data-driven improvements helped us connect better with our customers and showcase the quality we stand for.",
    },
    {
      name: 'Aida Al Awadi',
      role: 'Founder & CEO',
      company: 'Triple A',
      avatar: '',
      quote:
        'Skyfall Enterprises significantly enhanced our digital infrastructure, enabling Triple A to streamline our Golden Visa services and business solutions across the UAE. Their expertise in web development and data optimization has been instrumental in improving our client engagement and operational efficiency.',
    },
  ];

  return (
    <Box
      id='testimonials'
      sx={{
        background: (theme) => theme.palette.background.paper,
        py: { xs: 6, md: 10 },
        color: '#fff',
      }}
    >
      <Container maxWidth='lg'>
        <Typography
          variant='h4'
          component='h2'
          sx={{ textAlign: 'center', fontWeight: 700, mb: 2 }}
        >
          Testimonials
        </Typography>
        <Typography
          variant='body1'
          sx={{
            textAlign: 'center',
            mb: 6,
            maxWidth: '700px',
            mx: 'auto',
            color: (theme) => theme.palette.text.secondary,
          }}
        >
          Don't just take our word for it. Here's what our clients have to say
          about our services.
        </Typography>

        {/* Using Stack for layout instead of Grid */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          justifyContent='center'
          alignItems='stretch' // Ensure cards stretch to the same height if needed
        >
          {testimonials.map((testimonial, index) => (
            <Box
              key={index}
              sx={{
                width: { xs: '100%', md: 'calc(33.33% - 21.33px)' }, // Adjust width based on spacing (spacing=4 -> 32px, 3 items -> 2 gaps -> 64px / 3 = 21.33px)
                mb: { xs: 4, md: 0 }, // Add margin bottom on extra small screens
              }}
            >
              <TestimonialCard {...testimonial} />
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
